"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RENDERED_NAME_MAX_LENGTH = exports.DEFAULT_WINNER_MINIMUM_SPEND = void 0;
exports.getPlacementRatioLabelFromAspectRatio = getPlacementRatioLabelFromAspectRatio;
exports.getPlacementRatioLabelFromFileName = getPlacementRatioLabelFromFileName;
exports.getPlacementRatioLabelForAsset = getPlacementRatioLabelForAsset;
exports.stripAspectRatioSuffix = stripAspectRatioSuffix;
exports.displayAdName = displayAdName;
exports.parseMultiAspectFileName = parseMultiAspectFileName;
exports.parseCarouselSlideDesignator = parseCarouselSlideDesignator;
exports.findPreferredMultiAspectParentIndex = findPreferredMultiAspectParentIndex;
exports.buildMultiAspectGroupingPlan = buildMultiAspectGroupingPlan;
exports.findDuplicateKnownRatiosByGroup = findDuplicateKnownRatiosByGroup;
exports.getPlacementWarnings = getPlacementWarnings;
exports.buildPlacementCarouselCardsFromMultiAspectAssets = buildPlacementCarouselCardsFromMultiAspectAssets;
exports.buildPlacementCarouselsFromNamedAssets = buildPlacementCarouselsFromNamedAssets;
var monitoring_1 = require("./monitoring");
Object.defineProperty(exports, "DEFAULT_WINNER_MINIMUM_SPEND", { enumerable: true, get: function () { return monitoring_1.DEFAULT_WINNER_MINIMUM_SPEND; } });
// The inner separator is optional so compact tokens (`_916`, `_45`, `_11`)
// parse like their separated forms (`_9x16`, `_4_5`) — real batches name
// files both ways, and an unparsed variant keeps its suffix in the published
// ad name and falls out of multi-aspect grouping. Accepted trade-off: a name
// that legitimately ends in `_45`/`_11` (e.g. a version number) now reads as
// a ratio suffix. Longer trailing numbers (`_145`, `_2916`) still don't match
// because the separator must sit immediately before the token.
const FILE_NAME_RATIO_SUFFIX_REGEX = /^(.+?)(?:[_~\-:\s]+)(9[:x_~-]?16|4[:x_~-]?5|1[:x_~-]?1)$/i;
const CAROUSEL_SLIDE_DESIGNATOR_REGEX = /(?:^|[_~\-:\s])c(\d+)(?:[_~\-:\s]*)s(\d+)(?=$|[_~\-:\s])/gi;
const MEDIA_FILE_EXTENSION_REGEX = /\.(png|jpe?g|gif|webp|bmp|heic|mp4|mov|avi|mpe?g|webm)$/i;
const PLACEMENT_GROUP_STORIES_REELS = "Stories and Reels (Facebook, Instagram, Messenger)";
const PLACEMENT_GROUP_INSTREAM = "Facebook in-stream video (desktop)";
const REELS_MAX_SECONDS = 90;
const INSTREAM_DESKTOP_MAX_SECONDS = 15;
const INSTREAM_DESKTOP_MIN_SECONDS = 5;
const INSTAGRAM_STREAM_MAX_SECONDS = 60;
function normalizeGroupKey(baseName) {
    return baseName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
}
function ratioFromSuffixToken(ratioToken) {
    const normalizedToken = ratioToken.toLowerCase().replace(/[:x_~\-\s]/g, "");
    if (normalizedToken === "916") {
        return "RATIO_9_16";
    }
    if (normalizedToken === "45") {
        return "RATIO_4_5";
    }
    if (normalizedToken === "11") {
        return "RATIO_1_1";
    }
    return null;
}
function isKnownMultiAspectRatio(ratio) {
    return (ratio === "RATIO_9_16" || ratio === "RATIO_4_5" || ratio === "RATIO_1_1");
}
function getPlacementRatioLabelFromAspectRatio(aspectRatio) {
    if (aspectRatio === "RATIO_9_16") {
        return "ratio_9x16";
    }
    if (aspectRatio === "RATIO_4_5") {
        return "ratio_4x5";
    }
    if (aspectRatio === "RATIO_1_1") {
        return "ratio_1x1";
    }
    return null;
}
function getPlacementRatioLabelFromFileName(fileName) {
    const parsed = parseMultiAspectFileName(fileName);
    return getPlacementRatioLabelFromAspectRatio(parsed.aspectRatio);
}
function getPlacementRatioLabelForAsset(input) {
    var _a;
    return ((_a = getPlacementRatioLabelFromAspectRatio(input.aspectRatio)) !== null && _a !== void 0 ? _a : getPlacementRatioLabelFromFileName(input.fileName));
}
function stripAspectRatioSuffix(fileName) {
    const extMatch = fileName.match(/\.[^./\\]+$/);
    const ext = extMatch ? extMatch[0] : "";
    const nameWithoutExt = fileName.replace(/\.[^./\\]+$/, "");
    const match = FILE_NAME_RATIO_SUFFIX_REGEX.exec(nameWithoutExt);
    if (!match)
        return fileName;
    return match[1] + ext;
}
/**
 * Longest name we will send to an ad platform, and the cap on the naming
 * templates that produce those names.
 *
 * Lives here because both apps need it: the backend renderer bounds the name
 * it emits, the frontend counter and launch gate bound the template you type,
 * and `displayAdName` below bounds the label the UI shows for the same ad.
 * Those three drifting apart is the whole history of this number — NEW-1501,
 * NEW-1784, and a UI label that stopped matching the shipped ad name.
 *
 * 255 is the largest value every platform we launch to accepts. Their own
 * ceilings, checked against vendor docs in August 2026:
 *
 *   Google     256  (campaign and ad group; the binding constraint)
 *   Snapchat   375  (campaign name)
 *   Meta       400  (campaign, ad set, ad)
 *   TikTok     512  (ad group)
 *
 * So Google is what this is really pinned to, with one character to spare.
 * Raising it past 255 means checking Google first.
 */
exports.RENDERED_NAME_MAX_LENGTH = 255;
/**
 * Human-readable label for a creative-testing asset — what we show in our UI
 * and notifications. Matches the ad name produced on Meta / TikTok at launch
 * time when the batch uses the default `{{fileBase}}` ad-name template:
 * drops the trailing aspect-ratio suffix, drops the file extension, and
 * applies the same character cleanup as the launch-side ad-name renderer.
 * Batches with a custom ad-name template may diverge from this short label —
 * the actual platform ad name still uses the custom template at launch.
 */
function displayAdName(fileName) {
    const withoutSuffix = stripAspectRatioSuffix(fileName);
    const withoutExt = withoutSuffix.replace(/\.[^/.]+$/, "");
    return withoutExt
        .trim()
        .replace(/[^a-zA-Z0-9\s\-_./]/g, "")
        .replace(/\s+/g, " ")
        .substring(0, exports.RENDERED_NAME_MAX_LENGTH);
}
function parseMultiAspectFileName(fileName) {
    const fileNameWithoutExtension = fileName.replace(/\.[^./\\]+$/, "");
    const match = FILE_NAME_RATIO_SUFFIX_REGEX.exec(fileNameWithoutExtension);
    if (!match) {
        return {
            aspectRatio: "UNKNOWN",
            baseNameKey: null,
            isEligibleForGrouping: false,
        };
    }
    const [, base = "", ratioToken = ""] = match;
    const aspectRatio = ratioFromSuffixToken(ratioToken);
    if (!aspectRatio) {
        return {
            aspectRatio: "UNKNOWN",
            baseNameKey: null,
            isEligibleForGrouping: false,
        };
    }
    const baseNameKey = normalizeGroupKey(base);
    if (!baseNameKey) {
        return {
            aspectRatio: "UNKNOWN",
            baseNameKey: null,
            isEligibleForGrouping: false,
        };
    }
    return {
        aspectRatio,
        baseNameKey,
        isEligibleForGrouping: true,
    };
}
function parseCarouselSlideDesignator(fileName) {
    var _a, _b;
    const stem = stripAspectRatioSuffix(fileName).replace(MEDIA_FILE_EXTENSION_REGEX, "");
    const matches = [...stem.matchAll(CAROUSEL_SLIDE_DESIGNATOR_REGEX)];
    const match = matches[matches.length - 1];
    if (!match) {
        return {
            carouselNumber: null,
            slideNumber: null,
            carouselKey: null,
            cardGroupKey: null,
            isEligibleForCarouselGrouping: false,
        };
    }
    const carouselNumber = Number.parseInt((_a = match[1]) !== null && _a !== void 0 ? _a : "", 10);
    const slideNumber = Number.parseInt((_b = match[2]) !== null && _b !== void 0 ? _b : "", 10);
    if (!Number.isFinite(carouselNumber) ||
        !Number.isFinite(slideNumber) ||
        carouselNumber <= 0 ||
        slideNumber <= 0) {
        return {
            carouselNumber: null,
            slideNumber: null,
            carouselKey: null,
            cardGroupKey: null,
            isEligibleForCarouselGrouping: false,
        };
    }
    return {
        carouselNumber,
        slideNumber,
        carouselKey: `c${carouselNumber}`,
        cardGroupKey: `c${carouselNumber}_s${slideNumber}`,
        isEligibleForCarouselGrouping: true,
    };
}
function findPreferredMultiAspectParentIndex(assets) {
    if (assets.length === 0) {
        return -1;
    }
    const findIndexForLabel = (label) => assets.findIndex((asset) => getPlacementRatioLabelForAsset(asset) === label);
    const preferredIndex = findIndexForLabel("ratio_9x16") >= 0
        ? findIndexForLabel("ratio_9x16")
        : findIndexForLabel("ratio_4x5") >= 0
            ? findIndexForLabel("ratio_4x5")
            : findIndexForLabel("ratio_1x1") >= 0
                ? findIndexForLabel("ratio_1x1")
                : assets.findIndex((asset) => getPlacementRatioLabelForAsset(asset) !== null);
    return preferredIndex >= 0 ? preferredIndex : 0;
}
function buildMultiAspectGroupingPlan(fileNames, platform, groupKeyOverrides) {
    var _a;
    const parsedItems = fileNames.map((fileName) => parseMultiAspectFileName(fileName));
    const parsedCarouselDesignators = fileNames.map((fileName) => parseCarouselSlideDesignator(fileName));
    // When an override is present, use the override as the group key
    // but still use the filename-parsed aspect ratio
    const effectiveKeys = parsedItems.map((parsed, index) => {
        const parsedCarousel = parsedCarouselDesignators[index];
        if ((parsedCarousel === null || parsedCarousel === void 0 ? void 0 : parsedCarousel.isEligibleForCarouselGrouping) &&
            parsedCarousel.cardGroupKey) {
            // Explicit `_C#_S#_` filename designators take precedence over external
            // folder/group overrides so carousel slide grouping stays stable even
            // when every file lives in the same Drive subfolder.
            return {
                groupKey: normalizeGroupKey(parsedCarousel.cardGroupKey),
                isEligible: true,
            };
        }
        const override = groupKeyOverrides === null || groupKeyOverrides === void 0 ? void 0 : groupKeyOverrides[index];
        // A filename that encodes an aspect-ratio suffix (e.g. `_4x5`, `_9x16`)
        // is treated as the author's explicit grouping intent and wins over any
        // override — not just aspect-ratio subfolder names. A caller-supplied
        // override (even a human-readable one like "Concept_A") is ignored when
        // the filename parses, so pairs like `hero_9x16` + `hero_4x5` stay merged
        // under the shared filename base. Overrides still drive grouping for
        // files whose names lack a ratio suffix.
        if (override && !parsed.isEligibleForGrouping) {
            return {
                groupKey: normalizeGroupKey(override),
                isEligible: true,
            };
        }
        return {
            groupKey: parsed.baseNameKey,
            isEligible: parsed.isEligibleForGrouping,
        };
    });
    const defaultItems = parsedItems.map((parsed, index) => ({
        aspectRatio: parsed.aspectRatio,
        multiAspectGroupKey: effectiveKeys[index].groupKey,
        isParent: false,
        parentIndex: null,
    }));
    if (platform !== "META") {
        return defaultItems.map((item) => (Object.assign(Object.assign({}, item), { multiAspectGroupKey: null, isParent: false, parentIndex: null })));
    }
    const groups = new Map();
    effectiveKeys.forEach((ek, index) => {
        var _a;
        if (ek.isEligible && ek.groupKey) {
            const existing = (_a = groups.get(ek.groupKey)) !== null && _a !== void 0 ? _a : [];
            existing.push(index);
            groups.set(ek.groupKey, existing);
        }
    });
    for (const groupIndexes of groups.values()) {
        const knownRatios = new Set();
        for (const index of groupIndexes) {
            const ratio = (_a = parsedItems[index]) === null || _a === void 0 ? void 0 : _a.aspectRatio;
            if (isKnownMultiAspectRatio(ratio)) {
                knownRatios.add(ratio);
            }
        }
        // Multi-aspect grouping is valid only when a group has at least two
        // distinct known ratios (e.g. 9:16 + 4:5). Single-ratio sets launch as
        // standalone assets.
        if (knownRatios.size < 2) {
            for (const index of groupIndexes) {
                defaultItems[index] = Object.assign(Object.assign({}, defaultItems[index]), { isParent: false, parentIndex: null, multiAspectGroupKey: null });
            }
            continue;
        }
        const parentIndex = findPreferredMultiAspectParentIndex(groupIndexes.map((index) => { var _a; return ({
            aspectRatio: parsedItems[index].aspectRatio,
            fileName: (_a = fileNames[index]) !== null && _a !== void 0 ? _a : "",
        }); }));
        if (parentIndex < 0) {
            continue;
        }
        const resolvedParentIndex = groupIndexes[parentIndex];
        if (resolvedParentIndex === undefined) {
            continue;
        }
        const childIndexes = groupIndexes.filter((index) => {
            if (index === resolvedParentIndex)
                return false;
            const ratio = parsedItems[index].aspectRatio;
            return ratio !== "UNKNOWN";
        });
        if (childIndexes.length === 0) {
            continue;
        }
        defaultItems[resolvedParentIndex] = Object.assign(Object.assign({}, defaultItems[resolvedParentIndex]), { isParent: true, parentIndex: null });
        for (const childIndex of childIndexes) {
            defaultItems[childIndex] = Object.assign(Object.assign({}, defaultItems[childIndex]), { isParent: false, parentIndex: resolvedParentIndex });
        }
    }
    for (const item of defaultItems) {
        if (item.parentIndex === null && !item.isParent) {
            item.multiAspectGroupKey = null;
        }
    }
    return defaultItems;
}
function findDuplicateKnownRatiosByGroup(assets) {
    var _a;
    var _b, _c;
    const groupedRatios = new Map();
    for (const asset of assets) {
        const groupKey = (_a = asset.groupKey) === null || _a === void 0 ? void 0 : _a.trim();
        if (!groupKey) {
            continue;
        }
        const inferredRatio = asset.aspectRatio && asset.aspectRatio !== "UNKNOWN"
            ? asset.aspectRatio
            : parseMultiAspectFileName(asset.fileName).aspectRatio;
        if (!isKnownMultiAspectRatio(inferredRatio)) {
            continue;
        }
        const ratiosByGroup = (_b = groupedRatios.get(groupKey)) !== null && _b !== void 0 ? _b : new Map();
        const filesForRatio = (_c = ratiosByGroup.get(inferredRatio)) !== null && _c !== void 0 ? _c : [];
        filesForRatio.push(asset.fileName);
        ratiosByGroup.set(inferredRatio, filesForRatio);
        groupedRatios.set(groupKey, ratiosByGroup);
    }
    const violations = [];
    for (const [groupKey, ratios] of groupedRatios.entries()) {
        for (const [ratio, fileNames] of ratios.entries()) {
            if (fileNames.length > 1) {
                violations.push({
                    groupKey,
                    ratio,
                    fileNames,
                });
            }
        }
    }
    return violations;
}
function getPlacementWarnings(input) {
    const warnings = [];
    const has916 = input.allRatioLabels.includes("ratio_9x16");
    if (!has916) {
        warnings.push(`${PLACEMENT_GROUP_STORIES_REELS}: No 9:16 asset. These placements will use a cropped 4:5 or 1:1 asset; for best results add a 9:16 creative.`);
    }
    if (input.hasVideoAssets && input.videoDurationSeconds !== undefined) {
        if (has916 && input.videoDurationSeconds > REELS_MAX_SECONDS) {
            warnings.push(`${PLACEMENT_GROUP_STORIES_REELS}: Video is ${input.videoDurationSeconds}s. Reels typically work best under ${REELS_MAX_SECONDS}s; some Reels placements may not deliver.`);
        }
        if (input.videoDurationSeconds > INSTREAM_DESKTOP_MAX_SECONDS) {
            warnings.push(`${PLACEMENT_GROUP_INSTREAM}: Video is ${input.videoDurationSeconds}s. In-stream desktop only supports 5–15s; this placement will not deliver for this creative.`);
        }
        if (input.videoDurationSeconds < INSTREAM_DESKTOP_MIN_SECONDS &&
            input.videoDurationSeconds > 0) {
            warnings.push(`${PLACEMENT_GROUP_INSTREAM}: Video is ${input.videoDurationSeconds}s. In-stream desktop requires 5–15s; this placement may not deliver.`);
        }
        if (input.hasInstagramAccount &&
            input.videoDurationSeconds > INSTAGRAM_STREAM_MAX_SECONDS) {
            warnings.push(`Feed-like placements (Facebook Feed, Marketplace, Video Feeds, Search, Instagram Feed, Explore): Video is ${input.videoDurationSeconds}s. Instagram Feed/Stream API allows up to ${INSTAGRAM_STREAM_MAX_SECONDS}s; longer videos may be truncated or not deliver on Instagram.`);
        }
    }
    return warnings;
}
function placementCarouselEffectiveGroupKey(asset) {
    var _a;
    const trimmed = (_a = asset.multiAspectGroupKey) === null || _a === void 0 ? void 0 : _a.trim();
    if (trimmed) {
        const k = normalizeGroupKey(trimmed);
        return k || null;
    }
    const parsed = parseMultiAspectFileName(asset.fileName);
    if (parsed.isEligibleForGrouping && parsed.baseNameKey) {
        return parsed.baseNameKey;
    }
    return null;
}
function placementCarouselEffectiveAspectRatio(asset) {
    if (asset.aspectRatio && asset.aspectRatio !== "UNKNOWN") {
        return asset.aspectRatio;
    }
    return parseMultiAspectFileName(asset.fileName).aspectRatio;
}
function getPlacementCarouselCardSortMetadata(card) {
    const parsedFromPrimary = parseCarouselSlideDesignator(card.primary.fileName);
    const parsed = parsedFromPrimary.isEligibleForCarouselGrouping
        ? parsedFromPrimary
        : parseCarouselSlideDesignator(card.displayBaseName);
    return {
        slideNumber: parsed.slideNumber,
        groupKey: card.groupKey,
    };
}
function sortBuiltPlacementCards(cards) {
    return [...cards].sort((left, right) => {
        const leftMeta = getPlacementCarouselCardSortMetadata(left);
        const rightMeta = getPlacementCarouselCardSortMetadata(right);
        if (leftMeta.slideNumber !== null &&
            rightMeta.slideNumber !== null &&
            leftMeta.slideNumber !== rightMeta.slideNumber) {
            return leftMeta.slideNumber - rightMeta.slideNumber;
        }
        if (leftMeta.slideNumber !== null && rightMeta.slideNumber === null) {
            return -1;
        }
        if (leftMeta.slideNumber === null && rightMeta.slideNumber !== null) {
            return 1;
        }
        return leftMeta.groupKey.localeCompare(rightMeta.groupKey);
    });
}
function ratioSignatureFromSet(ratios) {
    return [...ratios].sort().join(",");
}
/**
 * Groups batch assets the same way as Meta multi-aspect single ads (filename suffix
 * `*_9x16` / `*_4x5` / `*_1x1` or `multiAspectGroupKey`), but each **group** becomes
 * one **carousel card** with `placementVariants` (one file per known ratio).
 *
 * Only groups with at least two distinct known ratios are candidates. If groups
 * differ in which ratios they include, the result keeps the **largest** subset of
 * groups that share the **exact same** ratio set (so Framework validation
 * “same ratios on every card” passes).
 */
function buildPlacementCarouselCardsFromMultiAspectAssets(assets) {
    var _a, _b;
    if (assets.length === 0) {
        return { ok: false, reason: "No assets to group." };
    }
    const byKey = new Map();
    for (const asset of assets) {
        const key = placementCarouselEffectiveGroupKey(asset);
        if (!key) {
            continue;
        }
        const list = (_a = byKey.get(key)) !== null && _a !== void 0 ? _a : [];
        list.push(asset);
        byKey.set(key, list);
    }
    const buckets = [];
    for (const [groupKey, members] of byKey) {
        const ratioToAsset = new Map();
        for (const member of members) {
            const ratio = placementCarouselEffectiveAspectRatio(member);
            if (!isKnownMultiAspectRatio(ratio)) {
                continue;
            }
            const existing = ratioToAsset.get(ratio);
            if (existing) {
                return {
                    ok: false,
                    reason: `Duplicate aspect ratio for one carousel card: group "${groupKey}" has two files for ${ratio} (${existing.fileName} and ${member.fileName}).`,
                };
            }
            ratioToAsset.set(ratio, member);
        }
        if (ratioToAsset.size >= 2) {
            buckets.push({ groupKey, ratioToAsset });
        }
    }
    if (buckets.length === 0) {
        return {
            ok: false,
            reason: "No multi-aspect groups found. Name files like `creative_9x16.mp4`, `creative_4x5.mp4` (same base name) or set a manual group key on each file, with at least two known aspect ratios per group.",
        };
    }
    const bySignature = new Map();
    for (const b of buckets) {
        const sig = ratioSignatureFromSet(b.ratioToAsset.keys());
        const arr = (_b = bySignature.get(sig)) !== null && _b !== void 0 ? _b : [];
        arr.push(b);
        bySignature.set(sig, arr);
    }
    let bestSig = "";
    let bestBuckets = [];
    for (const [sig, list] of bySignature) {
        if (list.length > bestBuckets.length) {
            bestSig = sig;
            bestBuckets = list;
            continue;
        }
        if (list.length === bestBuckets.length) {
            const newCount = sig.split(",").length;
            const oldCount = bestSig.split(",").length;
            if (newCount > oldCount) {
                bestSig = sig;
                bestBuckets = list;
            }
        }
    }
    if (bestBuckets.length === 0) {
        return {
            ok: false,
            reason: "Could not determine a shared aspect-ratio set for carousel cards.",
        };
    }
    const sortedBuckets = [...bestBuckets].sort((a, b) => a.groupKey.localeCompare(b.groupKey));
    const maxCards = 10;
    const truncated = sortedBuckets.length > maxCards;
    const limited = sortedBuckets.slice(0, maxCards);
    const cards = limited.map((bucket) => {
        var _a;
        var _b;
        const entries = [...bucket.ratioToAsset.entries()];
        const parentPick = findPreferredMultiAspectParentIndex(entries.map(([ar, a]) => ({
            aspectRatio: ar,
            fileName: a.fileName,
        })));
        const primary = (_b = (_a = entries[parentPick]) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : entries[0][1];
        const variants = entries
            .map(([aspectRatio, asset]) => ({ aspectRatio, asset }))
            .sort((x, y) => x.aspectRatio.localeCompare(y.aspectRatio));
        return {
            groupKey: bucket.groupKey,
            displayBaseName: stripAspectRatioSuffix(primary.fileName),
            primary,
            variants,
        };
    });
    return { ok: true, cards: sortBuiltPlacementCards(cards), truncated };
}
function buildPlacementCarouselsFromNamedAssets(assets) {
    var _a;
    if (assets.length === 0) {
        return { ok: false, reason: "No assets to group." };
    }
    const parsedDesignators = assets.map((asset) => parseCarouselSlideDesignator(asset.fileName));
    const eligibleAssets = parsedDesignators.filter((designator) => designator.isEligibleForCarouselGrouping).length;
    if (eligibleAssets === 0) {
        return {
            ok: false,
            reason: "No `C#S#` or `_C#_S#_` carousel designators found. Name files like `Concept_C1S1_9x16.png` or `Concept_C1_S1_4x5.png`.",
        };
    }
    if (eligibleAssets !== assets.length) {
        return {
            ok: false,
            reason: "All assets must follow the `C#S#` or `_C#_S#_` naming standard to auto-build carousels from names.",
        };
    }
    const byCarousel = new Map();
    for (let index = 0; index < assets.length; index++) {
        const asset = assets[index];
        const parsed = parsedDesignators[index];
        if (!(parsed === null || parsed === void 0 ? void 0 : parsed.carouselKey) || parsed.carouselNumber === null) {
            continue;
        }
        const existing = (_a = byCarousel.get(parsed.carouselKey)) !== null && _a !== void 0 ? _a : {
            carouselNumber: parsed.carouselNumber,
            assets: [],
        };
        existing.assets.push(asset);
        byCarousel.set(parsed.carouselKey, existing);
    }
    const carousels = [];
    const failedCarousels = [];
    for (const [carouselKey, entry] of [...byCarousel.entries()].sort((left, right) => left[1].carouselNumber - right[1].carouselNumber)) {
        const built = buildPlacementCarouselCardsFromMultiAspectAssets(entry.assets);
        if (!built.ok) {
            failedCarousels.push({
                carouselKey,
                carouselNumber: entry.carouselNumber,
                displayName: `Carousel ${entry.carouselNumber}`,
                reason: built.reason,
            });
            continue;
        }
        carousels.push({
            carouselKey,
            carouselNumber: entry.carouselNumber,
            displayName: `Carousel ${entry.carouselNumber}`,
            cards: built.cards,
            truncated: built.truncated,
        });
    }
    if (carousels.length === 0) {
        const failureSummary = failedCarousels
            .slice(0, 2)
            .map((failure) => `${failure.displayName}: ${failure.reason}`)
            .join(" ");
        return {
            ok: false,
            reason: failedCarousels.length > 0
                ? `No valid carousel groups could be created from the asset names. ${failureSummary}${failedCarousels.length > 2 ? ` ${failedCarousels.length - 2} more carousel(s) failed.` : ""}`.trim()
                : "No valid carousel groups could be created from the asset names.",
            failedCarousels,
        };
    }
    return { ok: true, carousels, failedCarousels };
}
